import Vue from "@/plugins"
import * as R from "ramda"
import {
    GET_VENUE_LIST,
    GET_UNSELECTED_VENUE_LIST,
    GET_JWT_TOKEN,
    GET_VENUE_BY_ID,
    HAS_SELECTED_MARKET,
    GET_SELECTED_MARKET,
    HAS_SELECTED_VENUE,
    GET_SELECTED_VENUE,
    GET_SELECTED_VENUE_TOTAL_BOX,
    GET_SELECTED_VENUE_TOTAL_ONLINE_BOX,
    GET_SELECTED_VENUE_TOTAL_OFFLINE_BOX,
    GET_SELECTED_VENUE_TOTAL_UPDATE_REQUIRED_BOX,
    GET_SELECTED_VENUE_ID,
    GET_SELECTED_VENUE_MARKET_ID,
    GET_VENUE_TOTAL_BOX_FN,
    GET_VENUE_TOTAL_ONLINE_BOX_FN,
    GET_VENUE_TOTAL_OFFLINE_BOX_FN,
    GET_VENUE_TOTAL_UPDATE_REQUIRED_BOX_FN,
} from "@/store/types/getters"
import {
    LOAD_VENUE_LIST,
    ADD_NEW_VENUE,
    CLEAR_VENUE_LIST,
    DELETE_VENUE,
    UPDATE_VENUE,
    UPDATE_SELECTED_VENUE,
    CLEAR_SELECTED_VENUE,
} from "@/store/types/mutations"
import {
    fetchVenueList,
    addNewVenue,
    deleteVenue,
    updateVenue,
} from "@/store/types/actions"
import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { Venue, AddVenue, UpdateVenue } from "@/models"
import { isEmpty } from "@/utils/validate"
import { notifyRequestError } from "@/utils/notify"
import { AxiosError, AxiosResponse } from "axios"
import { MarketModuleNamespace } from "@/consts"
import { isNull } from "lodash"

export interface State {
    venues: Venue[]
    selected: Venue | null
}

const state: State = {
    venues: [],
    selected: null,
}

const namespaced = true

const getters: GetterTree<State, any> = {
    [GET_VENUE_LIST](state: State): Venue[] {
        return state.venues
    },
    [GET_UNSELECTED_VENUE_LIST](state: State, getters: any): Venue[] {
        return !getters[HAS_SELECTED_VENUE]
            ? getters[GET_VENUE_LIST]
            : getters[GET_VENUE_LIST].filter(
                  (x: Venue) => x.id !== (state.selected as Venue).id
              )
    },
    [HAS_SELECTED_VENUE](state: State): boolean {
        return !isNull(state.selected)
    },
    [GET_SELECTED_VENUE](state: State): Venue | null {
        return state.selected
    },
    [GET_VENUE_BY_ID](state: State): (venueId: string) => Venue | undefined {
        return (venueId: string): Venue | undefined => {
            return state.venues.find((x: Venue) => x.id === venueId)
        }
    },
    [GET_SELECTED_VENUE_ID](state: State, getters: any): string | null {
        return getters[HAS_SELECTED_VENUE]
            ? getters[GET_SELECTED_VENUE].id
            : null
    },
    [GET_SELECTED_VENUE_MARKET_ID](state: State, getters: any): string | null {
        return getters[HAS_SELECTED_VENUE]
            ? getters[GET_SELECTED_VENUE].market_id
            : null
    },
    [GET_SELECTED_VENUE_TOTAL_ONLINE_BOX](
        state: State,
        getters: any,
        rootState: any,
        rootGetters: any
    ): number {
        return getters[HAS_SELECTED_VENUE]
            ? rootGetters[GET_VENUE_TOTAL_ONLINE_BOX_FN](
                  getters[GET_SELECTED_VENUE_MARKET_ID],
                  getters[GET_SELECTED_VENUE_ID]
              )
            : 0
    },
    [GET_SELECTED_VENUE_TOTAL_OFFLINE_BOX](
        state: State,
        getters: any,
        rootState: any,
        rootGetters: any
    ): number {
        return getters[HAS_SELECTED_VENUE]
            ? rootGetters[GET_VENUE_TOTAL_OFFLINE_BOX_FN](
                  getters[GET_SELECTED_VENUE_MARKET_ID],
                  getters[GET_SELECTED_VENUE_ID]
              )
            : 0
    },
    [GET_SELECTED_VENUE_TOTAL_BOX](
        state: State,
        getters: any,
        rootState: any,
        rootGetters: any
    ): number {
        return getters[HAS_SELECTED_VENUE]
            ? rootGetters[GET_VENUE_TOTAL_BOX_FN](
                  getters[GET_SELECTED_VENUE_MARKET_ID],
                  getters[GET_SELECTED_VENUE_ID]
              )
            : 0
    },
    [GET_SELECTED_VENUE_TOTAL_UPDATE_REQUIRED_BOX](
        state: State,
        getters: any,
        rootState: any,
        rootGetters: any
    ): number {
        return getters[HAS_SELECTED_VENUE]
            ? rootGetters[GET_VENUE_TOTAL_UPDATE_REQUIRED_BOX_FN](
                  getters[GET_SELECTED_VENUE_MARKET_ID],
                  getters[GET_SELECTED_VENUE_ID]
              )
            : 0
    },
}

const mutations: MutationTree<State> = {
    [LOAD_VENUE_LIST](state: State, payload: Venue[]) {
        state.venues = payload
    },
    [ADD_NEW_VENUE](state: State, payload: Venue) {
        state.venues.push(payload)
    },
    [DELETE_VENUE](state: State, payload: string) {
        if (!isNull(state.selected) && state.selected.id === payload) {
            state.selected = null
        }

        state.venues = R.filter(
            R.complement(R.propEq("id", payload)),
            state.venues
        )
    },
    [UPDATE_SELECTED_VENUE](state: State, payload: Venue) {
        state.selected = payload
    },
    [UPDATE_VENUE](state: State, payload: Venue) {
        const transform = (x: Venue) => {
            const newVenue = R.mergeDeepLeft(payload)(x)
            if (!isNull(state.selected) && state.selected.id === payload.id) {
                state.selected = newVenue
            }
            return newVenue
        }

        state.venues = R.map(R.when(R.propEq("id", payload.id), transform))(
            state.venues
        )
    },
    [CLEAR_SELECTED_VENUE](state: State) {
        state.selected = null
    },
    [CLEAR_VENUE_LIST](state: State) {
        state.venues = []
    },
}

const actions: ActionTree<State, any> = {
    [fetchVenueList]({ rootGetters, commit }) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return
        }

        if (!rootGetters[MarketModuleNamespace + "/" + HAS_SELECTED_MARKET]) {
            return
        }

        Vue.prototype
            .$api({
                method: "GET",
                url: `/api/v1/markets/${
                    rootGetters[
                        MarketModuleNamespace + "/" + GET_SELECTED_MARKET
                    ].id
                }/venues`,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<Venue[]>) => {
                commit(LOAD_VENUE_LIST, resp.data)
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to fetch venue list")
            })
    },
    [addNewVenue]({ rootGetters, commit }, payload: AddVenue) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return
        }

        if (!rootGetters[MarketModuleNamespace + "/" + HAS_SELECTED_MARKET]) {
            return
        }

        Vue.prototype
            .$api({
                method: "POST",
                url: `/api/v1/markets/${
                    rootGetters[
                        MarketModuleNamespace + "/" + GET_SELECTED_MARKET
                    ].id
                }/venues`,
                data: payload,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<Venue>) => {
                commit(ADD_NEW_VENUE, resp.data)
                Vue.prototype.$modal.hide("SShopForm")
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to add new venue")
            })
    },
    [deleteVenue]({ rootGetters, commit }, payload: string) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return
        }

        if (!rootGetters[MarketModuleNamespace + "/" + HAS_SELECTED_MARKET]) {
            return
        }

        Vue.prototype
            .$api({
                method: "DELETE",
                url: `/api/v1/markets/${
                    rootGetters[
                        MarketModuleNamespace + "/" + GET_SELECTED_MARKET
                    ].id
                }/venues/${payload}`,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<Venue>) => {
                commit(DELETE_VENUE, resp.data.id)
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to delete venue")
            })
    },
    [updateVenue]({ rootGetters, commit }, payload: UpdateVenue) {
        if (isEmpty(rootGetters[GET_JWT_TOKEN])) {
            return
        }

        if (!rootGetters[MarketModuleNamespace + "/" + HAS_SELECTED_MARKET]) {
            return
        }

        Vue.prototype
            .$api({
                method: "PUT",
                url: `/api/v1/markets/${
                    rootGetters[
                        MarketModuleNamespace + "/" + GET_SELECTED_MARKET
                    ].id
                }/venues/${payload.id}`,
                data: payload,
                headers: {
                    Authorization: "Bearer " + rootGetters[GET_JWT_TOKEN],
                },
            })
            .then((resp: AxiosResponse<Venue>) => {
                Vue.prototype.$modal.hide("SBLEBoxUpdateWorkingHoursForm")
                commit(UPDATE_VENUE, resp.data)
            })
            .catch((err: AxiosError) => {
                notifyRequestError(err, "Faied to update venue")
            })
    },
}

const module: Module<State, any> = {
    namespaced,
    state,
    getters,
    mutations,
    actions,
}

export default module
